import React, { useState, useEffect, ChangeEvent } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Button, StatusBadge } from 'react-covideo-common';
import { CheckboxInput, LoadingIndicator, NewModal } from 'lib/components';
import {
  fetchCustomerUser,
  fetchCustomerUsers,
  getAllDepartmentsForCustomer,
  searchCustomers,
} from 'lib/api';
import { Customer, useAuth } from 'lib/context';
import { mergeDeep } from 'lib/utils/object';
import {
  Content,
  Counter,
  CustomerItem,
  CustomerItemTitle,
  CustomerSelectionCountText,
  dropdownStyles,
  EmptyText,
  Footer,
  FormWrapper,
  Label,
  Left,
  ListPrivacyWrapper,
  Menu,
  MyCompanyLabel,
  PrivacyLevelText,
  PrivacyWrapper,
  Right,
  SelectAllText,
  SelectAllWrapper,
  SelectAndCount,
  SelectionCountText,
  Textarea,
  TitleChip,
  UserDetails,
  UserEmail,
  UserItem,
  UsersList,
  UserTitle,
} from './style';
import { DepartmentsCheckboxDropdown } from 'app/pages/reports/components';
import { Search } from 'lib/components';
import Select from 'react-select';
import { dropdownAccessData, OptionType } from './data';
import { useCreateFolderAccess } from 'lib/api/folderAccess/createFolderAccess';
import { ACCESS_RIGHTS } from 'lib/api/folderAccess/types';
import {
  CustomerType,
  DepartmentsMapType,
  DepartmentsResponse,
  SelectedCustomersType,
  UsersMapType,
  UserType,
} from './types';
import { IUsersCustomersResponse } from 'lib/api/users/getUsersCustomers';
import { successToast } from 'lib/components/toasts/success';
import { CustomerObjectType, SimplifiedUserObject } from './ResellerEditModal';
import { Folder } from 'lib/api/folders/getFolders';
import { MdOutlineUnfoldLess, MdOutlineUnfoldMore } from 'react-icons/md';
import { Gap } from '../styles/layout';
import { CheckboxLabel } from '../styles/typography';
import { SHARE_FOLDER_NOTIFY_CHECKBOX_LABEL } from 'lib/const/Folders';

const CHECKBOX_COLOR = 'rgba(0, 27, 83, 0.6)';

type Selection = {
  [key: string]: { [key: string]: boolean };
};

export type Viewer = {
  customerId: number;
  userId: number;
};

type Department = {
  label: string;
  value: string;
};

type Props = {
  initUsers?: string[];
  handleModalClose: (reportId?: string) => void;
  onSelect?: (userIds: string[]) => void;
  viewers?: Viewer[];
  folderId: string;
  setSelection?: (arg: SimplifiedUserObject[]) => void;
  skipCreateOnSave?: boolean;
  folder?: Folder;
  setIsSelectAll?: (value: boolean) => void;
  setGlobalPermission?: (value: string) => void;
  setIsCancel?: (value: boolean) => void;
  initialUsers?: CustomerObjectType | Selection;
  setSelectedCustomerIds?: (value: string[]) => void;
};

export const ResellerModal = (props: Props) => {
  const {
    userData: { customerId, userId },
  } = useAuth();

  const [loaded, setLoaded] = useState<boolean>(false);
  const [customers, setCustomers] = useState<CustomerType>({}); // all customer info
  const [customersCount, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [users, setUsers] = useState<UserType>({});
  const [usersTemp, setUsersTemp] = useState<UserType>({});
  const [usersMap, setUsersMap] = useState<UsersMapType>({}); // all customer-user info
  const [filteredUsers, setFilteredUsers] = useState<UserType>({});
  const [selectedUsers, setSelectedUsers] = useState({} as Selection);
  const [selectAll, setSelectAll] = useState<boolean>(
    !!props.folder?.includeNewUsers
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCustomers, setSelectedCustomers] =
    useState<SelectedCustomersType>({});
  const [departmentsMap, setDepartmentsMap] = useState<DepartmentsMapType>({});
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedAccess, setSelectedAccess] = useState<OptionType>(
    dropdownAccessData[0]
  );
  const [message, setMessage] = useState<string>('');
  const [isFutureUsersIncluded, setIsFutureUsersIncluded] = useState(
    !!props.folder?.includeNewUsers
  );
  const [sendNotification, setSendNotification] = useState<boolean>(false);

  let { handleModalClose, initUsers = [], viewers = [], folderId } = props;
  const {
    mutateAsync: createFolderAccess,
    isLoading: isLoadingCreateFolderAccess,
  } = useCreateFolderAccess();

  const isViewer = (userId = 0, customerId = 0) => {
    const cid = customerId || 0;
    const uid = userId || 0;
    if (!uid && !cid) return false;

    return !!viewers.find(
      (viewer: Viewer) =>
        (uid && viewer.userId === uid) || (cid && viewer.customerId === cid)
    );
  };

  const loadUsers = async () => {
    const selected = { ...selectedUsers };

    await Promise.all(
      initUsers.map(async u => {
        const user = await fetchCustomerUser(u);
        if (!selected[user.customerId]) {
          selected[user.customerId] = {};
        }
        selected[user.customerId][user.id] = true;
      })
    );

    setSelectedUsers(selected);
  };

  useEffect(() => {
    if (props.setSelection) {
      // find selected user ids
      const userIds: string[] = [];
      const selectedCustomerIds: string[] = [];
      Object.keys(selectedUsers).forEach(key => {
        const values = Object.keys(selectedUsers[key]);
        if (values.length) {
          selectedCustomerIds.push(key);
        }
        userIds.push(...values);
      });

      const newSelection: SimplifiedUserObject[] = [];

      Object.keys(users).forEach((cid: string) => {
        const userArray = users[Number(cid)];

        const userObjects = userArray
          .filter((user: IUsersCustomersResponse) =>
            userIds.includes(user.id.toString())
          )
          .map(user => {
            return {
              userId: user.id,
              access: selectedAccess.value as ACCESS_RIGHTS,
              customerId: Number(cid),
              firstName: user.firstName,
              lastName: user.lastName,
              departmentId: user?.DeptId?.toString() || '',
              departmentName: user?.Dept || '',
              business: customers[Number(cid)]?.business || '',
              email: user.email,
            };
          });

        newSelection.push(...userObjects);
      });
      if (props.setSelectedCustomerIds) {
        props.setSelectedCustomerIds(selectedCustomerIds);
      }
      props.setSelection(newSelection);
    }
  }, [selectedUsers, selectedAccess]);

  useEffect(() => {
    if (initUsers && initUsers.length) {
      loadUsers();
    }
  }, [initUsers]);

  const fetchCustomers = async (
    p: number = page,
    limit: number = 10
  ): Promise<any> => {
    const [_customers, count]: [Customer[], number] = await searchCustomers(
      searchQuery,
      true,
      p,
      limit
    );

    setPage(p + 1);
    setCount(count);

    let uM = { ...usersMap };
    let cstmrs = { ...customers };
    let sC = { ...selectedCustomers };
    let sU = { ...selectedUsers };
    let usrs = { ...users };
    let fU = { ...filteredUsers };
    let depart = { ...departmentsMap };

    return new Promise(async resolve => {
      await Promise.all(
        _customers.map(async (c: any) => {
          let _users = await fetchCustomerUsers(c.customerId);
          // Remove logged user in order to avoid sharing a folder with yourself
          _users = _users.filter(
            (item: { id: number }) => item.id !== parseInt(userId)
          );
          _users.sort((userA: any, userB: any) => {
            if (userA.firstName < userB.firstName) {
              return -1;
            }
            if (userA.firstName > userB.firstName) {
              return 1;
            }
            return 0;
          });

          const deptUsed = {};
          const uMap = _users.reduce((obj: any, u: any) => {
            obj[u.id] = c.customerId;

            if (u.DeptId && u.Dept) {
              if (!depart[c.customerId]) {
                depart[c.customerId] = [];
              }
              // @ts-ignore
              if (!deptUsed[u.DeptId]) {
                depart[c.customerId].push({
                  value: u.DeptId,
                  label: u.Dept,
                });
                // @ts-ignore
                deptUsed[u.DeptId] = true;
              }
            }
            return obj;
          }, {});

          (depart[c.customerId] || []).sort(
            (departmentA: any, departmentB: any) => {
              if (
                departmentA.label.toLowerCase() <
                departmentB.label.toLowerCase()
              ) {
                return -1;
              }
              if (
                departmentA.label.toLowerCase() >
                departmentB.label.toLowerCase()
              ) {
                return 1;
              }
              return 0;
            }
          );

          uM = { ...uM, ...uMap };
          cstmrs[c.customerId] = c;
          sC[c.customerId] = false;
          sU[c.customerId] = sU[c.customerId] || {};
          usrs[parseInt(c.customerId)] = _users;
          fU[c.customerId] = _users;
        })
      );

      setUsersMap(uM);
      setCustomers(cstmrs);
      setSelectedCustomers(sC);
      setSelectedUsers(selected => mergeDeep(selected, sU) as Selection);
      setUsers(usrs);
      setUsersTemp(usrs);
      setFilteredUsers(fU);
      setLoaded(true);
      setDepartmentsMap(depart);
      resolve([fU, usrs]);
    });
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (Object.keys(customers).length && !departments.length) {
      fetchDepartments();
    }
  }, [customers]);

  useEffect(() => {
    if (props.initialUsers) {
      setSelectedUsers(props.initialUsers as Selection);
    }
    if (props.setIsSelectAll) {
      props.setIsSelectAll(isFutureUsersIncluded);
    }
  }, [props.initialUsers]);

  const toggleUser = (userId: string, customerId: string) => {
    const selected = { ...selectedUsers };

    if (selected[customerId] && selected[customerId][userId]) {
      delete selected[customerId][userId];
    } else {
      selected[customerId][userId] = true;
    }
    setSelectedUsers(selected);
  };

  const toggleCustomer = (customerId: number) => {
    const selected = { ...selectedUsers };
    const filteredIds = filteredUsers[customerId].map(fU => fU.id);

    if (
      Object.values(selected[customerId]).length &&
      userExistsInSelected(customerId)
    ) {
      filteredIds.forEach(fId => {
        delete selected[customerId][fId];
      });
    } else {
      filteredIds.forEach(fId => {
        selected[customerId][fId] = true;
      });
    }
    setSelectedUsers(selected);
  };

  const filterByIndividualDepartment = (
    selectedDept: string[],
    cid: number
  ) => {
    let filtered = JSON.parse(JSON.stringify(usersTemp));

    if (selectedDept?.length) {
      const newUsers = filtered[cid].filter(
        (u: IUsersCustomersResponse) =>
          !!selectedDept.find(
            dept => dept.toString() === (u?.DeptId?.toString() || '')
          )
      );

      filtered[cid] = newUsers;
    }
    setUsers(filtered);
    setFilteredUsers(filtered);
  };

  const searchForCustomers = async (query: string = '') => {
    query = query.toLowerCase();
    setSearchQuery(query);
    const [cst, cnt] = await searchCustomers(query, true, 0, 10);
    setCount(cnt);

    let uM = { ...usersMap };
    let cstmrs = { ...customers };
    let sC = { ...selectedCustomers };
    let sU = { ...selectedUsers };
    let usrs = { ...users };
    let fU: any = {};

    return new Promise(async resolve => {
      await Promise.all(
        cst.map(async (c: any) => {
          // const _users = await fetchCustomerUsers(c.customerId);
          const _users = c.users;
          _users.sort((userA: any, userB: any) => {
            if (userA.firstName < userB.firstName) {
              return -1;
            }
            if (userA.firstName > userB.firstName) {
              return 1;
            }
            return 0;
          });

          const uMap = c.users.reduce((obj: any, u: any) => {
            obj[u.id] = c.customerId;
            return obj;
          }, {});

          uM = { ...uM, ...uMap };
          cstmrs[c.customerId] = c;
          sC[c.customerId] = false;
          sU[c.customerId] = sU[c.customerId] || {};
          usrs[c.customerId] = _users;
          fU[c.customerId] = _users;
        })
      );

      setUsersMap(uM);
      setCustomers(cstmrs);
      setSelectedCustomers(sC);
      setSelectedUsers(sU);
      setUsers(usrs);
      setUsersTemp(usrs);
      setFilteredUsers(fU);
      setLoaded(true);
      resolve([fU, usrs]);
    });
  };

  const handleCreateFolderAccess = async () => {
    let userIds = [];
    let selectedCustomerIds = [];
    for (const [key, value] of Object.entries(selectedUsers)) {
      for (const [key2] of Object.entries(value)) {
        userIds.push(parseInt(key2));
      }
      if (Object.entries(value).length) {
        selectedCustomerIds.push(key);
      }
    }
    const accessObject = {
      folderId: parseInt(folderId),
      users: userIds
        .filter(item => !viewers.some(i => i.userId === item))
        .map(id => ({
          userId: id,
          access: selectedAccess.value as ACCESS_RIGHTS,
        })),
      message,
      selectedCustomerIds,
      isSelectAll: isFutureUsersIncluded,
      folderAccessPermission: selectedAccess.value,
      sendNotification,
    };

    const access = await createFolderAccess(accessObject);
    if (access) {
      successToast({ title: 'Successfully shared folder!' });
      handleModalClose();
    }
  };

  const toggleSelectAll = async () => {
    const value = !selectAll;
    const selected = { ...selectedUsers };
    if (value) {
      Object.keys(customers).forEach((c: any) => {
        selected[c] = filteredUsers[c].reduce((obj: any, u: any) => {
          obj[u.id] = true;
          return obj;
        }, {});
      });
    } else {
      Object.keys(customers).forEach((c: any) => {
        selected[c] = {};
      });
    }
    setSelectAll(value);
    setSelectedUsers(selected);
  };

  const toggleFutureUsers = () => {
    const selected = { ...selectedUsers };
    const value = !isFutureUsersIncluded;
    setIsFutureUsersIncluded(value);
    if (props.setIsSelectAll) {
      props.setIsSelectAll(value);
    }
    setSelectedUsers(selected);
  };

  const isAllSelected = (customerId: number) => {
    //get all filtered users which are selectable
    const selectableUsers = filteredUsers[customerId];

    //get the number of selected filtered users
    const filteredSelected = selectableUsers.filter(({ id }) =>
      Object.keys(selectedUsers[customerId]).includes(id.toString())
    );
    //check if all filtered users are selected
    return {
      status: filteredSelected.length === selectableUsers.length,
      selectedUsers: filteredSelected.length,
      selectableUsers: selectableUsers.length,
    };
  };

  const userExistsInSelected = (customerId: number) => {
    const filteredIds = filteredUsers[customerId].map(fU => fU.id);
    const filter = Object.keys(selectedUsers[customerId]).filter(sU =>
      filteredIds.includes(parseInt(sU))
    );
    return !!filter.length;
  };

  useEffect(() => {
    let isViewerFiltered: UserType = {};
    Object.assign(isViewerFiltered, filteredUsers);

    const filteredSelected = Object.keys(isViewerFiltered).map((c: any) => {
      return isViewerFiltered[c].filter(({ id }) =>
        Object.keys(selectedUsers[c]).includes(id.toString())
      );
    });

    //check if all filtered users are selected
    let count: number = 0;
    Object.keys(isViewerFiltered).forEach((c: any, index: any) => {
      if (
        Object.keys(selectedUsers[c]).length !== 0 &&
        filteredSelected[index].length === Object.keys(selectedUsers[c]).length
      ) {
        count += 1;
      }
    });

    !!count && count === filteredSelected.length
      ? setSelectAll(true)
      : setSelectAll(false);
  }, [selectedUsers, filteredUsers]);

  const usersCount = () => {
    const tempSelectedUsers: Selection = {};
    Object.assign(tempSelectedUsers, selectedUsers);
    let total: number = 0;

    Object.keys(tempSelectedUsers).forEach((customerId: string) => {
      const userObject = tempSelectedUsers[Number(customerId)];
      const users = Object.keys(userObject);
      total += users.length;
    });

    return total;
  };

  const searchResults = Object.values(filteredUsers).reduce(
    (sum: number, usrs: any) => sum + (Object.keys(usrs).length ? 1 : 0),
    0
  );

  const handleChange = (e: any) => {
    setSelectedAccess(e);
    if (props.setGlobalPermission) {
      props.setGlobalPermission(e.value);
    }
  };

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  };

  const fetchDepartments = async () => {
    // fetch for all customers
    const customerIds = Object.keys(customers).map((customerId: string) =>
      Number(customerId)
    );

    const allDepartments: Department[] = [];
    await Promise.allSettled(
      customerIds.map(async (customerId: number) => {
        try {
          const departmentsResponse = (await getAllDepartmentsForCustomer(
            customerId
          )) as DepartmentsResponse;
          const { data: customerDepts } = departmentsResponse;
          if (customerDepts.length) {
            const parsedCustomerDepts = customerDepts.map(dept => ({
              label: dept.name.trim(),
              value: dept.id.toString(),
            }));
            allDepartments.push(...parsedCustomerDepts);
          }
          setDepartments(allDepartments);
        } catch (e) {
          console.log(e);
        }
      })
    );
  };

  const getFilteredSelectedUsersNum = (c: number) => {
    let counter: number = 0;

    Object.keys(selectedUsers[c]).forEach(sU => {
      filteredUsers[c].forEach(tFU => {
        if (sU === tFU.id.toString()) counter++;
      });
    });
    return counter;
  };

  const isLoading = !loaded || isLoadingCreateFolderAccess;

  return (
    <NewModal
      headerText='Share folder'
      hideHeader={false}
      closeModalOnBackdropOrEscPress={true}
      closeModal={() => {
        if (props.setIsCancel) {
          props.setIsCancel(true);
        }
        handleModalClose();
      }}
      headerMargin='0 0 0 0'
      style={{
        content: {
          padding: '24px 24px 16px 24px',
          minWidth: 'auto',
          paddingLeft: '32px',
        },
      }}
    >
      <div>
        <Content>
          <Menu>
            <div style={{ width: '392px' }}>
              <Search
                placeholder='Search users or company…'
                onSearch={searchForCustomers}
                bgColor='#fffff'
              />
            </div>
          </Menu>
          <SelectAndCount>
            <SelectAllWrapper>
              <CheckboxInput
                checked={selectAll}
                onChange={toggleSelectAll}
                disabled={!Object.keys(filteredUsers).length}
              />
              <SelectAllText>Select all</SelectAllText>
            </SelectAllWrapper>
            <SelectAllWrapper>
              <CheckboxInput
                checked={isFutureUsersIncluded}
                onChange={toggleFutureUsers}
                disabled={false}
              />
              <SelectAllText>Include New Users</SelectAllText>
            </SelectAllWrapper>
            <SelectionCountText>
              <Counter>{usersCount()} selected</Counter>
            </SelectionCountText>
          </SelectAndCount>
          <ListPrivacyWrapper>
            <Left>
              <>
                <UsersList style={{ overflowY: 'hidden' }}>
                  {isLoading && (
                    <LoadingIndicator isLoading={true} height='300px' />
                  )}
                  {!isLoading && !searchResults && !!searchQuery.length && (
                    <EmptyText>Your search didn’t match any company</EmptyText>
                  )}
                  {!isLoading && (
                    <InfiniteScroll
                      className='infinite-scroll'
                      dataLength={Object.keys(filteredUsers).length}
                      next={fetchCustomers}
                      hasMore={
                        customersCount > Object.keys(filteredUsers).length
                      }
                      loader={<h4>Loading...</h4>}
                      height={300}
                    >
                      {Object.keys(filteredUsers).map((c: any) => (
                        <div key={c}>
                          <CustomerItem>
                            <CustomerItemTitle
                              onClick={() =>
                                setSelectedCustomers((cs: any) => ({
                                  ...cs,
                                  [c]: !selectedCustomers[c],
                                }))
                              }
                            >
                              {!selectedCustomers[c] && (
                                <MdOutlineUnfoldMore color={CHECKBOX_COLOR} />
                              )}
                              {selectedCustomers[c] && (
                                <MdOutlineUnfoldLess color={CHECKBOX_COLOR} />
                              )}

                              <CheckboxInput
                                checkGroupIndicator={
                                  isAllSelected(c).selectedUsers > 0 &&
                                  !isAllSelected(c).status
                                }
                                checked={
                                  selectedUsers[c] && isAllSelected(c).status
                                }
                                onChange={() => {
                                  toggleCustomer(c);
                                }}
                              />
                              <span style={{ marginLeft: '8px' }}>
                                {customers[c] && customers[c].business}
                              </span>

                              {c === customerId && (
                                <MyCompanyLabel>My Company</MyCompanyLabel>
                              )}

                              <CustomerSelectionCountText>
                                (
                                {selectedUsers[c]
                                  ? getFilteredSelectedUsersNum(c)
                                  : 0}
                                /{filteredUsers[c].length})
                              </CustomerSelectionCountText>
                            </CustomerItemTitle>

                            <DepartmentsCheckboxDropdown
                              departments={departmentsMap[c]}
                              onDepartmentsSelected={departments => {
                                filterByIndividualDepartment(departments, c);
                              }}
                              menuShouldBlockScroll={false}
                            />
                          </CustomerItem>

                          {selectedCustomers[c] &&
                            filteredUsers[c].map(
                              (user: IUsersCustomersResponse) => (
                                <UserItem
                                  key={user.id}
                                  disabled={false}
                                  marginLeft='80px'
                                >
                                  <CheckboxInput
                                    checked={!!selectedUsers[c][user.id]}
                                    onChange={() => {
                                      toggleUser(user.id.toString(), c);
                                    }}
                                    disabled={false}
                                  />
                                  <UserDetails>
                                    <TitleChip>
                                      <UserTitle
                                        onClick={() => {
                                          toggleUser(user.id.toString(), c);
                                        }}
                                        width='fit-content'
                                      >
                                        {`${user.firstName} ${user.lastName}`}
                                      </UserTitle>
                                      {isViewer(user.id) && (
                                        <StatusBadge
                                          text={'Viewer'}
                                          size={'small'}
                                          variant='info'
                                        />
                                      )}
                                    </TitleChip>
                                    <UserEmail>{`(${user.email})`}</UserEmail>
                                  </UserDetails>
                                </UserItem>
                              )
                            )}
                        </div>
                      ))}
                    </InfiniteScroll>
                  )}
                </UsersList>
              </>
            </Left>
            <Right>
              <PrivacyWrapper>
                <PrivacyLevelText>
                  Set privacy level for selected users
                </PrivacyLevelText>
                <FormWrapper>
                  <Label>Access</Label>
                  <Select
                    isSearchable={false}
                    placeholder='Select Option'
                    value={selectedAccess}
                    options={dropdownAccessData}
                    onChange={handleChange}
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    styles={dropdownStyles}
                    isDisabled={isLoading}
                  />
                </FormWrapper>
                <FormWrapper>
                  <Label>Add Message (optional)</Label>
                  <Textarea
                    value={message}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      handleTextChange(e);
                    }}
                    style={{
                      padding: '8px',
                      boxSizing: 'border-box',
                    }}
                    disabled={isLoading}
                  />
                </FormWrapper>
              </PrivacyWrapper>
            </Right>
          </ListPrivacyWrapper>
          <Footer>
            <Gap gap='12px'>
              <CheckboxInput
                id='toggleSendNotification'
                disabled={isLoading}
                checked={sendNotification}
                onChange={() => setSendNotification(prev => !prev)}
              />
              <CheckboxLabel htmlFor='toggleSendNotification'>
                {SHARE_FOLDER_NOTIFY_CHECKBOX_LABEL}
              </CheckboxLabel>
            </Gap>
            <Button
              text='Cancel'
              variant='secondary'
              onClick={() => {
                handleModalClose();
                if (props.setIsCancel) {
                  props.setIsCancel(true);
                }
              }}
              disabled={isLoading}
            />
            <Button
              text='Share with Selected Users'
              variant='primary'
              onClick={() => {
                if (!props.skipCreateOnSave) {
                  return handleCreateFolderAccess();
                }
                handleModalClose();
              }}
              disabled={!usersCount || isLoading}
            />
          </Footer>
        </Content>
      </div>
    </NewModal>
  );
};
